// ccw 2026-09-28 msl-map: Interactive MSL territory map + liaison cards (/resources/msl).
// Map and cards share one piece of state, the focused liaison; everything else is derived.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactGA from "react-ga4";

import MslMap from './MslMap';
import MslCard from './MslCard';
import { US_STATES } from '../constants/mslStates';
import { MSL_TERRITORIES, MSL_LIAISONS, MSL_UNASSIGNED_COLOR, MSL_UNASSIGNED_MESSAGE } from '../constants/mslTerritories';

// GA4 dwell threshold, so a pointer sweeping across the map doesn't flood events.
const GA_DWELL_MS = 500

// Detect touch by capability, not screen width, so touch laptops and iPads behave correctly.
const HOVER_NONE_QUERY = '(hover: none)'
const useHoverNone = () => {
	const [hoverNone, setHoverNone] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(HOVER_NONE_QUERY).matches : false)
	useEffect(() => {
		if (!window.matchMedia) return
		const mql = window.matchMedia(HOVER_NONE_QUERY)
		const onChange = (e) => setHoverNone(e.matches)
		mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange)
		return () => mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange)
	}, [])
	return hoverNone
}

const MslMapPage = (props) => {
	const { msl } = props
	const isTouch = useHoverNone()

	// focus.liaisonId drives all highlighting; focus.stateId is the hovered/tapped state (tooltip,
	// info bar, outline); focus.input records how focus was set so each input only clears its own.
	const [focus, setFocus] = useState({ liaisonId: null, stateId: null, input: null })
	const [announcement, setAnnouncement] = useState('')
	const gaTimer = useRef(null)

	// Lookup tables derived from config + API data.
	const model = useMemo(() => {
		const territoryById = {}
		const stateTerritory = {}
		MSL_TERRITORIES.forEach(t => {
			territoryById[t.id] = t
			t.states.forEach(s => { stateTerritory[s] = t.id })
		})
		const stateName = {}
		US_STATES.forEach(s => { stateName[s.id] = s.name })

		// Config sets order and territories; the API supplies the person's details. Emails are
		// trimmed: the live table has stray whitespace (e.g. " ahuff@neurelis.com").
		const normEmail = (email) => (email || '').trim().toLowerCase()
		const apiByEmail = {}
		const matchedEmails = {}
		;(msl || []).forEach(cur => { if (normEmail(cur.email)) apiByEmail[normEmail(cur.email)] = cur })

		const liaisons = []
		MSL_LIAISONS.forEach(cfg => {
			const api = apiByEmail[normEmail(cfg.email)]
			if (!api) return // Not in the MSL table (inactive or not loaded yet), so no card.
			matchedEmails[normEmail(cfg.email)] = true
			const territories = cfg.territories.filter(t => territoryById[t.id]).map(t => ({ ...territoryById[t.id], note: t.note }))
			liaisons.push({ ...api, email: api.email.trim(), id: cfg.id, territories })
		})
		// MSLs in the table but not in the config still get a card, but have no territories, so
		// they don't highlight anything (see hasTerritories).
		const unmatched = (msl || []).filter(cur => !matchedEmails[normEmail(cur.email)])
		unmatched.forEach(cur => {
			liaisons.push({ ...cur, email: (cur.email || '').trim(), id: 'msl-' + cur.mslID, territories: [] })
		})
		if (msl && msl.length && unmatched.length) {
			console.warn('MSL map: no territory config for ' + unmatched.map(cur => cur.name + ' <' + normEmail(cur.email) + '>').join(', ') + '. Add them to MSL_LIAISONS in constants/mslTerritories.js.')
		}

		const liaisonById = {}
		const territoryLiaison = {}
		liaisons.forEach(l => {
			liaisonById[l.id] = l
			l.territories.forEach(t => { territoryLiaison[t.id] = l.id })
		})

		return { territoryById, stateTerritory, stateName, liaisons, liaisonById, territoryLiaison }
	}, [msl])

	const { territoryById, stateTerritory, stateName, liaisons, liaisonById, territoryLiaison } = model

	const focusedLiaison = focus.liaisonId ? liaisonById[focus.liaisonId] : null
	const focusedTerritoryIds = useMemo(() => focusedLiaison ? focusedLiaison.territories.map(t => t.id) : [], [focusedLiaison])

	// Text for the tooltip / info bar / live region when a state is hovered or tapped.
	const stateInfo = useMemo(() => {
		if (!focus.stateId) return null
		const territoryId = stateTerritory[focus.stateId]
		const territory = territoryId ? territoryById[territoryId] : null
		const liaison = territoryId && territoryLiaison[territoryId] ? liaisonById[territoryLiaison[territoryId]] : null
		let text = MSL_UNASSIGNED_MESSAGE
		if (territory && liaison) text = liaison.name + ' · ' + territory.label
		else if (territory) text = territory.label
		return {
			stateId: focus.stateId,
			name: stateName[focus.stateId] || focus.stateId,
			text,
			color: territory ? territory.color : MSL_UNASSIGNED_COLOR,
			territory,
			liaison,
		}
	}, [focus.stateId, stateTerritory, territoryById, territoryLiaison, liaisonById, stateName])

	// Polite live-region announcement for whatever is focused.
	useEffect(() => {
		if (stateInfo) {
			setAnnouncement(stateInfo.name + ': ' + stateInfo.text)
		} else if (focusedLiaison && focusedLiaison.territories.length) {
			setAnnouncement(focusedLiaison.name + ' covers ' + focusedLiaison.territories.map(t => t.label).join(' and '))
		} else if (!focus.liaisonId) {
			setAnnouncement('')
		}
	}, [stateInfo, focusedLiaison, focus.liaisonId])

	// GA4 ----------------------------------------------------------------------------------
	const clearGaTimer = () => {
		if (gaTimer.current) {
			clearTimeout(gaTimer.current)
			gaTimer.current = null
		}
	}
	useEffect(() => clearGaTimer, [])

	const sendStateHover = useCallback((stateId) => {
		const territoryId = stateTerritory[stateId]
		const liaisonId = territoryId ? territoryLiaison[territoryId] : null
		ReactGA.event('msl_map_state_hover', {
			state: stateId,
			territory: territoryId ? territoryById[territoryId].label : 'unassigned',
			liaison: liaisonId ? liaisonById[liaisonId].name : 'none',
		})
	}, [stateTerritory, territoryLiaison, territoryById, liaisonById])

	const sendCardFocus = useCallback((liaisonId, input) => {
		const liaison = liaisonById[liaisonId]
		if (liaison) ReactGA.event('msl_card_focus', { liaison: liaison.name, input })
	}, [liaisonById])

	// Map handlers -------------------------------------------------------------------------
	const fncStateFocus = (stateId) => {
		if (stateId === focus.stateId) return
		const territoryId = stateTerritory[stateId]
		const liaisonId = territoryId && territoryLiaison[territoryId] ? territoryLiaison[territoryId] : null
		setFocus({ liaisonId, stateId, input: isTouch ? 'touch' : 'pointer' })
		clearGaTimer()
		gaTimer.current = setTimeout(() => sendStateHover(stateId), GA_DWELL_MS)
	}

	const fncMapClear = () => {
		clearGaTimer()
		setFocus({ liaisonId: null, stateId: null, input: null })
	}

	// Card handlers ------------------------------------------------------------------------
	// A liaison with no territories has nothing to highlight, so their card doesn't take focus
	// (otherwise it would dim the whole map and every other card).
	const hasTerritories = (liaisonId) => !!(liaisonById[liaisonId] && liaisonById[liaisonId].territories.length)

	const fncCardPointerEnter = (liaisonId) => {
		if (isTouch || !hasTerritories(liaisonId)) return
		setFocus({ liaisonId, stateId: null, input: 'pointer' })
		clearGaTimer()
		gaTimer.current = setTimeout(() => sendCardFocus(liaisonId, 'pointer'), GA_DWELL_MS)
	}

	const fncCardPointerLeave = () => {
		if (isTouch) return
		if (focus.input === 'pointer') fncMapClear()
	}

	const fncCardKeyboardFocus = (liaisonId) => {
		if (!hasTerritories(liaisonId)) return
		if (focus.liaisonId === liaisonId && focus.input === 'keyboard') return
		clearGaTimer()
		setFocus({ liaisonId, stateId: null, input: 'keyboard' })
		sendCardFocus(liaisonId, 'keyboard')
	}

	const fncCardKeyboardBlur = (liaisonId) => {
		if (focus.liaisonId === liaisonId && focus.input === 'keyboard') fncMapClear()
	}

	const fncCardTap = (liaisonId) => {
		if (!isTouch || !hasTerritories(liaisonId)) return
		clearGaTimer()
		if (focus.liaisonId === liaisonId && !focus.stateId) {
			setFocus({ liaisonId: null, stateId: null, input: null })
		} else {
			setFocus({ liaisonId, stateId: null, input: 'touch' })
			sendCardFocus(liaisonId, 'touch')
		}
	}

	const fncEmailClick = (liaisonId) => {
		const liaison = liaisonById[liaisonId]
		if (liaison) ReactGA.event('msl_email_click', { liaison: liaison.name })
	}

	return (
		<div className="msl-map-page">
			{/* ccw 2026-09-28 msl-map: on touch, the tapped state's info replaces the hint line (above
			    the map) instead of a bar under it, so the cards don't shift off-screen (client feedback). */}
			{isTouch && stateInfo
				? <p className="msl-hint msl-info-bar" aria-hidden="true">
					<span className="msl-dot" style={{ background: stateInfo.color }}></span>
					<span>{stateInfo.name}: {stateInfo.text}</span>
				</p>
				: <p className="msl-hint">
					{isTouch ? 'Tap a state or a liaison to see coverage.' : 'Hover over a state or a liaison to see their territory.'}
				</p>
			}

			<div className="msl-layout">
				<div className="msl-map-column">
					<MslMap
						states={US_STATES}
						territoryById={territoryById}
						stateTerritory={stateTerritory}
						focusedTerritoryIds={focusedTerritoryIds}
						hasFocus={!!focus.liaisonId}
						stateInfo={stateInfo}
						isTouch={isTouch}
						fncStateFocus={fncStateFocus}
						fncMapClear={fncMapClear}
					/>

					<div className="msl-sr-only" aria-live="polite">{announcement}</div>
				</div>

				<div className={'msl-cards' + (focus.liaisonId ? ' has-focus' : '')}>
					{liaisons.map(liaison => (
						<MslCard
							key={liaison.id}
							liaison={liaison}
							stateName={stateName}
							isFocused={focus.liaisonId === liaison.id}
							fncPointerEnter={fncCardPointerEnter}
							fncPointerLeave={fncCardPointerLeave}
							fncKeyboardFocus={fncCardKeyboardFocus}
							fncKeyboardBlur={fncCardKeyboardBlur}
							fncTap={fncCardTap}
							fncEmailClick={fncEmailClick}
						/>
					))}
				</div>
			</div>
		</div>
	)
}

export default MslMapPage
